import { Injectable } from '@angular/core';
import {
  FormBuilderTypeSafe,
  FormGroupTypeSafe,
} from 'angular-typesafe-reactive-forms-helper';
import {
  IOdabirPaketaDto,
  IPackageDto,
  SubscriptionPackage,
} from '@kodit/core/data-api';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class OdabirPaketaService {
  private _form: OdabirPaketaForm | undefined = undefined;
  shouldDisableKorisnickiNaloziAddition = true;
  shouldDisableProdajnoMestoAddition = true;
  shouldDisableProizvodnjaAddition = true;
  shouldDisableFinansijeAddition = true;

  // pravila za disablovanja glavnih paketa
  mainPackageDisableRules: {
    [key in SubscriptionPackage]?: SubscriptionPackage[];
  } = {
    [SubscriptionPackage.BESPLATNO]: [
      SubscriptionPackage.EFAKTURE,
      SubscriptionPackage.VELEPRODAJA,
    ],
    [SubscriptionPackage.EFAKTURE]: [
      SubscriptionPackage.BESPLATNO,
      SubscriptionPackage.VELEPRODAJA,
      SubscriptionPackage.MALOPRODAJA,
    ],
    [SubscriptionPackage.VELEPRODAJA]: [
      SubscriptionPackage.BESPLATNO,
      SubscriptionPackage.EFAKTURE,
      SubscriptionPackage.FISKALNA_KASA,
    ],
    [SubscriptionPackage.ARHIVA]: [],
    [SubscriptionPackage.FISKALNA_KASA]: [
      SubscriptionPackage.VELEPRODAJA,
      SubscriptionPackage.MALOPRODAJA,
    ],
    [SubscriptionPackage.MALOPRODAJA]: [
      SubscriptionPackage.EFAKTURE,
      SubscriptionPackage.FISKALNA_KASA,
    ],
  };

  constructor(private _fb: FormBuilderTypeSafe) {}

  getForm() {
    if (!this._form) {
      this._form = this._fb.group<IOdabirPaketaDto>({
        packages: this._initPackagesArray([]),
        dodatniKorisnici: this._fb.control(0),
        dodatnaProdajnaMesta: this._fb.control(0),
        isYearly: this._fb.control(true),
      });
    }
    return this._form;
  }

  patchForm(dto: IOdabirPaketaDto) {
    this._form.patchValue({
      dodatniKorisnici: dto.dodatniKorisnici ?? 0,
      dodatnaProdajnaMesta: dto.dodatnaProdajnaMesta ?? 0,
      isYearly: dto.isYearly ?? true,
    });

    (this._form.controls.packages as FormArray).clear();
    dto.packages.forEach((packageItem) => {
      (this._form.controls.packages as FormArray).push(
        this._initPackageGroup(packageItem)
      );
    });

   this._enableOrDisableAdditions(dto.packages);
  }

  private _enableOrDisableAdditions(packages: IPackageDto[]) {
    this.shouldDisableKorisnickiNaloziAddition = !packages.some(
      (x) => x.packageType === SubscriptionPackage.EFAKTURE ||
        x.packageType === SubscriptionPackage.VELEPRODAJA ||
        x.packageType === SubscriptionPackage.ARHIVA
    );
    this.shouldDisableProdajnoMestoAddition = !packages.some(
      (x) => x.packageType === SubscriptionPackage.FISKALNA_KASA ||
        x.packageType === SubscriptionPackage.MALOPRODAJA
    );
    this.shouldDisableProizvodnjaAddition = !packages.some(
      (x) => x.packageType === SubscriptionPackage.EFAKTURE ||
        x.packageType === SubscriptionPackage.VELEPRODAJA ||
        x.packageType === SubscriptionPackage.FISKALNA_KASA ||
        x.packageType === SubscriptionPackage.MALOPRODAJA
    );
    this.shouldDisableFinansijeAddition = !packages.some(
      (x) => x.packageType === SubscriptionPackage.EFAKTURE ||
        x.packageType === SubscriptionPackage.VELEPRODAJA ||
        x.packageType === SubscriptionPackage.FISKALNA_KASA ||
        x.packageType === SubscriptionPackage.MALOPRODAJA ||
        x.packageType === SubscriptionPackage.BESPLATNO
    );
  }

  reset() {
    this._form = undefined;
  }

  private _initPackagesArray(
    packages?: IPackageDto[],
    jeBasePackage?: boolean
  ): FormArray {
    if (!packages || packages.length === 0) {
      return this._fb.array([], {
        validators: jeBasePackage
          ? Validators.required
          : Validators.nullValidator,
      });
    }
    const result: FormArray = this._fb.array([]);

    packages.forEach((packageItem) => {
      result.push(this._initPackageGroup(packageItem));
    });

    return result;
  }

  private _initPackageGroup(
    model?: IPackageDto
  ): FormGroupTypeSafe<IPackageDto> {
    return this._fb.group<IPackageDto>({
      packageId: new FormControl(model?.packageId),
      price: new FormControl(model?.price),
      priceLabel: new FormControl(model?.priceLabel),
      packageType: new FormControl(model?.packageType),
      label: new FormControl(model?.label),
      isAddition: new FormControl(model?.isAddition),
      freeSeats: new FormControl(model?.freeSeats),
    });
  }

  getFreeSeats(): number {
    if (this.getPackages.length == 0) {
      return 0;
    }
    return (
      (this.getPackages.value as IPackageDto[]).reduce((x, y) => {
        return x + y.freeSeats;
      }, 0) + this.getForm().value.dodatnaProdajnaMesta
    );
  }

  handlePackageSelect(packageDto: IPackageDto, additionalPackages?: number) {
    switch (packageDto.packageType) {
      case SubscriptionPackage.BESPLATNO:
        this._addPackage(packageDto);
        break;

      case SubscriptionPackage.EFAKTURE:
      case SubscriptionPackage.VELEPRODAJA:
        this._addPackage(packageDto);
        break;

      case SubscriptionPackage.KORISNICKI_NALOG:
        this.getForm().controls.dodatniKorisnici.patchValue(
          additionalPackages ?? 0
        );
        this._addPackage(packageDto, additionalPackages);
        break;

      case SubscriptionPackage.FISKALNA_KASA:
      case SubscriptionPackage.MALOPRODAJA:
        this._addPackage(packageDto);
        break;

      case SubscriptionPackage.PRODAJNO_MESTO:
        this.getForm().controls.dodatnaProdajnaMesta.patchValue(
          additionalPackages ?? 0
        );
        this._addPackage(packageDto, additionalPackages);
        break;

      default:
        this._addPackage(packageDto, additionalPackages);
        break;
    }

    this._enableOrDisableAdditions(this._form.value.packages);
  }

  handlePackageUnselect(packageDto: IPackageDto, additionalPackages?: number) {
    switch (packageDto.packageType) {
      case SubscriptionPackage.BESPLATNO:
        this._removePackage(SubscriptionPackage.BESPLATNO);
        this._removePackage(SubscriptionPackage.FINANSIJE);
        // this.shouldDisableFinansijeAddition = false;
        break;

      case SubscriptionPackage.EFAKTURE:
      case SubscriptionPackage.VELEPRODAJA:
        this._removePackage(SubscriptionPackage.EFAKTURE);
        this._removePackage(SubscriptionPackage.VELEPRODAJA);
        this._removePackage(SubscriptionPackage.KORISNICKI_NALOG);
        this._removePackage(SubscriptionPackage.EFAKTURE_API);
        this._removePackage(SubscriptionPackage.PROIZVODNJA);
        this._removePackage(SubscriptionPackage.FINANSIJE);
        this.getForm().controls.dodatniKorisnici.patchValue(0);
        break;

      case SubscriptionPackage.FISKALNA_KASA:
      case SubscriptionPackage.MALOPRODAJA:
        this._removePackage(SubscriptionPackage.FISKALNA_KASA);
        this._removePackage(SubscriptionPackage.MALOPRODAJA);
        this._removePackage(SubscriptionPackage.PRODAJNO_MESTO);
        this._removePackage(SubscriptionPackage.FISKLANI_API);
        this.getForm().controls.dodatnaProdajnaMesta.patchValue(0);
        break;

      case SubscriptionPackage.KORISNICKI_NALOG:
        this._decreasePackageNumber(packageDto, additionalPackages);
        this.getForm().controls.dodatniKorisnici.patchValue(
          additionalPackages ?? 0
        );
        break;

      case SubscriptionPackage.PRODAJNO_MESTO:
        this._decreasePackageNumber(packageDto, additionalPackages);
        this.getForm().controls.dodatnaProdajnaMesta.patchValue(
          additionalPackages ?? 0
        );
        break;

      default:
        this._removePackage(packageDto.packageType);
        break;
    }
    this._enableOrDisableAdditions(this._form.value.packages);
  }

  private _updatePackagePrice(idx: number, price: number) {
    (this.getPackages.at(idx) as FormGroup).controls.price.patchValue(price);
  }

  private _updatePackageLabel(idx: number, label: string) {
    (this.getPackages.at(idx) as FormGroup).controls.label.patchValue(label);
  }

  private _addPackage(packageDto: IPackageDto, additionalPackages?: number) {
    const idx = this.getPackages.value.findIndex(
      (x: IPackageDto) => x.packageType === packageDto.packageType
    );
    if (idx > -1) {
      this._updatePackagePrice(idx, packageDto.price * additionalPackages);
      this._updatePackageLabel(
        idx,
        `${packageDto.label} (${additionalPackages})`
      );
    } else {
      if (additionalPackages > 1) {
        new Array(additionalPackages)
          .fill(0)
          .forEach((p: number, idx: number) => {
            if (idx === 0) {
              this.getPackages.push(this._initPackageGroup(packageDto));
            } else {
              this._updatePackagePrice(
                0,
                packageDto.price * additionalPackages
              );
              this._updatePackageLabel(
                0,
                `${packageDto.label} (${additionalPackages})`
              );
            }
          });
      } else {
        this.getPackages.push(this._initPackageGroup(packageDto));
      }
    }
  }

  private _removePackage(type: SubscriptionPackage) {
    const idx = this.getPackages.value.findIndex(
      (x: IPackageDto) => x.packageType === type
    );
    if (idx > -1) {
      this.getPackages.removeAt(idx);
    }
  }

  private _decreasePackageNumber(
    packageDto: IPackageDto,
    numOfPackages: number
  ) {
    const idx = this.getPackages.value.findIndex(
      (x: IPackageDto) => x.packageType === packageDto.packageType
    );
    const currentlyCount =
      this.getPackages.at(idx).value.price / packageDto.price;
    if (currentlyCount > 1) {
      this._updatePackagePrice(idx, packageDto.price * numOfPackages);
      this._updatePackageLabel(idx, `${packageDto.label} (${numOfPackages})`);
    } else {
      this.getPackages.removeAt(idx);
    }
  }

  // metoda koja odredjuje koje glavne pakete treba disabled
  getDisabledMainPackages(): Set<SubscriptionPackage> {
    const disabled = new Set<SubscriptionPackage>();
    (this.getPackages.value as IPackageDto[])
      .filter((p) => !p.isAddition)
      .forEach((p) => {
        const toDisable = this.mainPackageDisableRules[p.packageType];
        if (toDisable) {
          toDisable.forEach((pkg) => disabled.add(pkg));
        }
      });
    return disabled;
  }

  clearPackages() {
    this.getPackages.clear();
  }

  get getPackages(): FormArray {
    if (!this._form) {
      return new FormArray([]);
    }
    return this._form.controls.packages as FormArray;
  }

  get getPackagesForFixedTotal(): IPackageDto[] {
    if (!this._form) {
      return [];
    }

    return (this._form.controls.packages.value as IPackageDto[])
      .filter(
        (x) =>
          x.packageType !== SubscriptionPackage.FISKLANI_API &&
          x.packageType !== SubscriptionPackage.EFAKTURE_API
      )
      .sort((a, b) => (a.isAddition as any) - (b.isAddition as any));
  }

  hasPackagesForFixed(): boolean {
    return this.getPackagesForFixedTotal.length > 0;
  }

  hasPackagesForUsage(): boolean {
    return this.getPackagesForUsageTotal.length > 0;
  }

  get getPackagesForUsageTotal(): IPackageDto[] {
    if (!this._form) {
      return [];
    }
    return (this._form.controls.packages.value as IPackageDto[])
      .filter(
        (x) =>
          x.packageType === SubscriptionPackage.FISKLANI_API ||
          x.packageType === SubscriptionPackage.EFAKTURE_API
      )
      .sort((a, b) => (a.isAddition as any) - (b.isAddition as any));
  }

  get getIsYearlyObservable() {
    return this._form.controls.isYearly.valueChanges;
  }

  hasYearlyPackages() {
    return this.getPackagesForFixedTotal.some((x) =>
      x.priceLabel.includes('godišnje')
    );
  }
}

export declare type OdabirPaketaForm = FormGroupTypeSafe<IOdabirPaketaDto>;
